'use client'

import { createContext, type ReactNode, useCallback, useContext, useEffect, useState } from 'react'
import { CaretLeft } from '@/components/icons/CaretLeft'
import { CaretRight } from '@/components/icons/CaretRight'
import { cn } from '@/lib/styles'
import { getColorVariant } from '@/utils/colorMapping'
import type {
  CarouselButtonProps,
  CarouselItemProps,
  CarouselNavigationProps,
  CarouselProps,
} from './carousel.types'

type CarouselContextType = {
  currentIndex: number
  totalItems: number
  updateIndex: (index: number) => void
  updateTotalItems: (total: number) => void
}

const CarouselContext = createContext<CarouselContextType | null>(null)

function useCarouselContext(): CarouselContextType {
  const context = useContext(CarouselContext)
  if (!context) {
    return {
      currentIndex: 0,
      totalItems: 0,
      updateIndex: () => {},
      updateTotalItems: () => {},
    }
  }
  return context
}

export function Carousel({ className, children }: CarouselProps) {
  const [currentIndex, setCurrentIndex] = useState(0)
  const [totalItems, setTotalItems] = useState(0)

  const updateIndex = useCallback((index: number) => {
    setCurrentIndex(index)
  }, [])

  const updateTotalItems = useCallback((total: number) => {
    setTotalItems(total)
  }, [])

  const contextValue: CarouselContextType = {
    currentIndex,
    totalItems,
    updateIndex,
    updateTotalItems,
  }

  return (
    <CarouselContext.Provider value={contextValue}>
      <div className={cn('w-full', className)} data-slot="carousel">
        {children}
      </div>
    </CarouselContext.Provider>
  )
}

export function CarouselItems({
  className,
  children,
}: {
  className?: string
  children: ReactNode
}) {
  const { currentIndex, updateTotalItems } = useCarouselContext()

  // Count carousel items
  useEffect(() => {
    const items = Array.isArray(children) ? children : []
    updateTotalItems(items.length)
  }, [children, updateTotalItems])

  return (
    <div className={cn('w-full overflow-hidden', className)} data-slot="carousel-items">
      <div
        className="flex w-full"
        style={{
          transform: `translateX(-${currentIndex * 100}%)`,
          transition: 'transform 0.3s ease-in-out',
        }}
      >
        {children}
      </div>
    </div>
  )
}

export function CarouselItem({ className, children }: CarouselItemProps) {
  return (
    <div className={cn('w-full flex-none basis-full flex', className)} data-slot="carousel-item">
      {children}
    </div>
  )
}

export function CarouselControls({
  className,
  children,
}: {
  className?: string
  children: ReactNode
}) {
  return (
    <div
      className={cn('flex gap-2 justify-center items-center', className)}
      data-slot="carousel-controls"
    >
      {children}
    </div>
  )
}

export function CarouselPreviousButton({
  className,
  children = <CaretLeft className="text-neutral-500 size-5" />,
  onClick,
  disabled,
}: CarouselButtonProps) {
  const { currentIndex, totalItems, updateIndex } = useCarouselContext()

  const handleClick = useCallback(() => {
    if (onClick) {
      onClick()
    } else {
      if (totalItems > 0) {
        updateIndex((currentIndex - 1 + totalItems) % totalItems)
      }
    }
  }, [onClick, currentIndex, totalItems, updateIndex])

  return (
    <button
      type="button"
      className={cn(
        'shadow-md rounded-xs py-1 px-2',
        disabled || totalItems <= 1
          ? 'bg-neutral-200 cursor-not-allowed'
          : 'bg-white cursor-pointer',

        className,
      )}
      onClick={handleClick}
      disabled={disabled || totalItems <= 1}
      data-slot="carousel-previous-button"
    >
      {children}
    </button>
  )
}

export function CarouselNextButton({
  className,
  children = <CaretRight className="text-neutral-500 size-5" />,
  onClick,
  disabled,
}: CarouselButtonProps) {
  const { currentIndex, totalItems, updateIndex } = useCarouselContext()

  const handleClick = useCallback(() => {
    if (onClick) {
      onClick()
    } else {
      if (totalItems > 0) {
        // Modulo used to go back to first slide when reaching the last item and clicking next
        updateIndex((currentIndex + 1) % totalItems)
      }
    }
  }, [onClick, currentIndex, totalItems, updateIndex])

  return (
    <button
      type="button"
      className={cn(
        'shadow-md rounded-xs py-1 px-2',
        disabled || totalItems <= 1
          ? 'bg-neutral-200 cursor-not-allowed'
          : 'bg-white cursor-pointer',

        className,
      )}
      onClick={handleClick}
      disabled={disabled || totalItems <= 1}
      data-slot="carousel-next-button"
    >
      {children}
    </button>
  )
}

export function CarouselItemNav({
  className,
  activeColor,
  currentIndex: propCurrentIndex,
  totalItems: propTotalItems,
  onItemClick,
}: CarouselNavigationProps) {
  const { currentIndex, totalItems, updateIndex } = useCarouselContext()

  const finalCurrentIndex = propCurrentIndex ?? currentIndex
  const finalTotalItems = propTotalItems ?? totalItems

  const handleItemClick = useCallback(
    (index: number) => {
      if (onItemClick) {
        onItemClick(index)
      } else {
        updateIndex(index)
      }
    },
    [onItemClick, updateIndex],
  )

  const bgColorClassName = getColorVariant(activeColor, 'bg', 'brand-salmon')

  return (
    <div className={cn('flex gap-1', className)} data-slot="carousel-nav">
      {Array.from({ length: finalTotalItems }, (_, index) => (
        <button
          type="button"
          // biome-ignore lint/suspicious/noArrayIndexKey: Static array from Array.from, no reordering or modification
          key={index}
          onClick={() => handleItemClick(index)}
          aria-label={`Go to item ${index + 1}`}
          className={cn('w-3 h-3 rounded-full border-none cursor-pointer', [
            index === finalCurrentIndex ? bgColorClassName : 'bg-neutral-300',
          ])}
        />
      ))}
    </div>
  )
}
